import {log} from "../utilities/logger";
import {callApi} from "./api";
import * as contract from "../contract";
import {makeBrowserSecret, rememberVerifier, takeVerifier} from "../utilities/sso";
import {Capacitor} from "@capacitor/core";
import {sessionStorage} from "./sessionStorage";
import getApiProxy from "../proxies/apiProxy";


// Signing in sets a browser cookie, or stores the native session securely. Each of these throws if the server refuses (the caller
// shows its message with apiErrorMessage) or can't be reached (isNetworkError).

// Browsers authenticate with a cookie; Android sends an opaque session token.
const clientKind = () => (Capacitor.isNativePlatform() ? 'native' as const : 'web' as const);

export const signIn = async (email: string, password: string) => {
    await sessionStorage.save(await callApi(contract.auth.login, { body: { email, password, client: clientKind() } }));
    log.info('signed in');
};

// Signs in with a new temporary account. The server gives it a sample board, with times of day in this browser's time zone;
// returns that board's id (absent if the board could not be made).
export const signInAsGuest = async () => {
    const { boardId, ...tokens } = await callApi(contract.auth.temporaryLogin, { body: { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, client: clientKind() } });
    await sessionStorage.save(tokens);
    log.info('signed in with a temporary account');

    return boardId;
};

// Which providers (Google, Apple) the server has set up, for the login page's buttons.
export const getSsoProviders = async () => {
    const { providers } = await callApi(contract.auth.getSsoProviders);
    return providers;
};

// Begins signing in with a provider: keeps a secret for this tab, tells the server its hash, and returns where to send the browser.
export const startSso = async (provider: contract.SsoProvider) => {
    const { verifier, challenge } = await makeBrowserSecret();
    const { url } = await callApi(contract.auth.ssoStart, { body: { provider, challenge } });
    rememberVerifier(verifier);
    log.info('signing in with a provider', { provider });

    return url;
};

// The end of it, when the provider has sent the browser back with a code: trades the code and the kept secret for a session.
// Throws if there is no secret (the sign-in was begun in another browser, or too long ago) or the server refuses.
export const finishSso = async (code: string) => {
    const verifier = takeVerifier();
    if (!verifier) throw new Error('This sign-in was not started in this browser. Please start it again here.');

    await sessionStorage.save(await callApi(contract.auth.ssoExchange, { body: { code, verifier, client: clientKind() } }));
    log.info('signed in with a provider');
};

export const signUp = async (email: string, password: string) => {
    await callApi(contract.auth.signup, { body: { email, password } });
    log.info('signed up');
};

// The browser cookie can only be cleared by the server. Surface a failed sign-out so the user can retry.
export const logout = async () => {
    const api = await getApiProxy();
    await api.post('api/logout', {});
    await sessionStorage.clear();
};

export const getUser = async () => {
    try {
        const { user } = await callApi(contract.account.getAccount);
        return user;
    } catch (error) {
        // Only an invalid/expired session means the session itself is bad —
        // a 403 (e.g. a kiosk-restricted account) just means this page isn't for them.
        if (error.response?.status === 401) {
            await sessionStorage.clear();
            return null;
        }
        if (error.response?.status === 403) return null;
        throw error;
    }
};

// Validate the cookie/native session with the server; no browser storage marker can prove authentication.
export const isUserAuthenticated = async () => (await callApi(contract.auth.currentSession)).authenticated;

export async function updateUserQuery(updatedAttributes: contract.AccountChanges) {
    try {
        return await callApi(contract.account.updateAccount, { body: { updatedAttributes } });
    } catch (error) {
        // An invalid session ends it; any other failure (validation, a network blip) must not sign the person out.
        if (error.response?.status === 401) await sessionStorage.clear();
        throw error;
    }
}

// Deletes the signed-in person's own account and everything it owns, for good. A regular account gives its password again; a temporary
// account has none. The native credential is dropped here too: the account it belongs to is gone.
export const deleteAccount = async (password?: string) => {
    await callApi(contract.account.deleteAccount, { body: password ? { password } : {} });
    await sessionStorage.clear();
    log.info('account deleted');
};

// Asks for a link to reset a forgotten password to be emailed to the address. The answer is the same whether or not an account has it.
export const requestPasswordReset = async (email: string) => {
    await callApi(contract.auth.requestPasswordReset, { body: { email } });
    log.info('password reset asked for');
};

// Sets a new password with the token from the emailed link. Every sign-in from before is ended, so the person signs in again.
export const confirmPasswordReset = async (token: string, password: string) => {
    await callApi(contract.auth.confirmPasswordReset, { body: { token, password } });
    await sessionStorage.clear();
    log.info('password reset');
};

